import {
  Box,
  Button,
  Center,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useDisclosure,
  useToast
} from "@chakra-ui/react";
import { DataTable } from "../../../components/DataTable";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SupplierCategoryModal } from "./index";
import {
  supplierCategoriesKeys,
  useCreateSupplierCategory, useDeleteSupplierCategory,
  useGetPaginatedSupplierCategories,
  useUpdateSupplierCategory
} from "../../../hooks/query";
import { hideAppSpinner, showAppSpinner } from "../../../context/Store/slices/appSpinnerSlice";
import {
  calculateDataTableState,
  constructQueryParamsString,
  getSortDirection,
  handlePageSizeChange,
  showErrorToast,
  showSuccessToast
} from "../../../utils";
import { useDispatch } from "react-redux";
import { useQueryClient } from "react-query";
import { FaBars, FaPen, FaTrash } from "react-icons/fa";
import { DeleteDialog } from "../../DataEntryPlanning/components/DeleteDialog";

function SupplierCategories() {
  const { t } = useTranslation("translation", {
    keyPrefix: "supplierSettings.supplierCategories"
  });
  const toast = useToast();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const {
    isOpen: isConfirmDialogOpen,
    onOpen: onConfirmDialogOpen,
    onClose: onConfirmDialogClose
  } = useDisclosure();
  const {
    isOpen: isCategoryModalOpen,
    onOpen: onCategoryModalOpen,
    onClose: onCategoryModalClose
  } = useDisclosure();

  const [categoriesList, setCategoriesList] = useState([]);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [categoryModalData, setCategoryModalData] = useState(null);

  const [dataTableState, setDataTableState] = useState({
    queryPageIndex: 0,
    queryPageSize: 10,
    queryPageSortBy: ["lastModifiedDate,desc"],
    totalElements: 0,
    totalPages: 0,
    filterObj: {}
  });

  const {
    totalPages,
    filterObj,
    queryPageIndex,
    queryPageSize,
    queryPageSortBy,
    totalElements
  } = dataTableState;

  const manageDataTableState = (newValObject) => {
    setDataTableState((prevState) => {
      return {
        ...prevState,
        ...calculateDataTableState({
          prevState,
          newValObject,
          initialSortingParam: "lastModifiedDate"
        })
      };
    });
  };

  const getQueryParamsString = () => {
    const queryParams = {
      page: queryPageIndex,
      size: queryPageSize,
      ...filterObj,
      sort: queryPageSortBy
    };
    return constructQueryParamsString(queryParams);
  };

  const {
    isFetching: isFetchingCategories,
    refetch: refetchCategories
  } = useGetPaginatedSupplierCategories(getQueryParamsString(), {
      onSuccess: (data) => {
        const pageContent = data?.content ?? [];
        const categories = pageContent.map((el) => {
          return {
            id: el.id,
            name: el.name
          };
        });
        setCategoriesList(categories);
        handlePageSizeChange(data, queryPageIndex, manageDataTableState);
      },
      onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
      cacheTime: 0
    }
  );

  const getRequestConfig = (successMessage) => {
    return {
      onMutate: () => dispatch(showAppSpinner()),
      onSuccess: () => {
        if (isCategoryModalOpen) {
          closeCategoryModal();
        }
        showSuccessToast(toast, successMessage);
        queryClient.invalidateQueries(supplierCategoriesKeys.paginatedSupplierCategories());
      },
      onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
      onSettled: () => dispatch(hideAppSpinner())
    };
  };

  const { mutate: createCategory } = useCreateSupplierCategory(getRequestConfig(t("messages.categoryCreated")));
  const { mutate: updateCategory } = useUpdateSupplierCategory(getRequestConfig(t("messages.categoryUpdated")));

  const handleAddNewCategory = () => {
    onCategoryModalOpen();
  };

  const closeCategoryModal = () => {
    setCategoryModalData(null);
    onCategoryModalClose();
  };

  const handleCategoryModalSubmit = (id, data) => {
    if (typeof id !== "undefined") {
      updateCategory({ id, data });
    } else {
      createCategory(data);
    }
  };

  const columns = useMemo(() => {
      function handleEditCategory(data) {
        setCategoryModalData(data.row.original);
        onCategoryModalOpen();
      }

      function handleDelete(rowId) {
        setSelectedRowId(rowId);
        onConfirmDialogOpen();
      }

      return [
        {
          header: t("gridHeaders.label"),
          accessorKey: "name",
          size: 180,
          sortDirection: getSortDirection(queryPageSortBy, "name")
        },
        {
          header: <div style={{ textAlign: "left" }}><b>{t("gridHeaders.actions")}</b></div>,
          accessorKey: "action",
          size: 30,
          enableColumnFilter: false,
          isSortable: false,
          cell: row => (
            <Menu isLazy>
              <MenuButton
                as={IconButton}
                aria-label="Actions"
                icon={<FaBars />}
                variant="outline"
              />
              <MenuList>
                <MenuItem
                  icon={<FaPen color="#6EA161" />}
                  onClick={() => handleEditCategory(row)}
                >
                  {t("buttons.editCategory")}
                </MenuItem>
                <MenuItem
                  icon={<FaTrash color="red" opacity="0.6" />}
                  onClick={() => handleDelete(row.row.original.id)}
                >
                  {t("buttons.deleteCategory")}
                </MenuItem>
              </MenuList>
            </Menu>
          )
        }
      ];
    }, [t, queryPageSortBy, onCategoryModalOpen, onConfirmDialogOpen]
  );

  const { mutate: deleteCategory } = useDeleteSupplierCategory({
    onMutate: () => dispatch(showAppSpinner()),
    onSuccess: () => {
      onConfirmDialogClose();
      setSelectedRowId(null);
      showSuccessToast(toast, t("messages.categoryDeleted"));
      queryClient.invalidateQueries(supplierCategoriesKeys.paginatedSupplierCategories());
      refetchCategories();
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => {
      dispatch(hideAppSpinner());
    }
  });

  const handleCategoryDelete = () => {
    if (selectedRowId !== null) {
      deleteCategory(selectedRowId);
    }
  };

  return (
    <Box marginBlockStart="5">
      <Center marginBlockEnd="5">
        <Button
          colorScheme="customScheme"
          width="40"
          onClick={handleAddNewCategory}
        >
          + {t("buttons.addNewCategory")}
        </Button>
      </Center>

      {isCategoryModalOpen && <SupplierCategoryModal
        modalData={categoryModalData}
        isOpen={isCategoryModalOpen}
        onClose={closeCategoryModal}
        onSubmit={handleCategoryModalSubmit}
      />}

      <DataTable
        data={categoriesList}
        columns={columns}
        isLoading={isFetchingCategories}
        serverSidePagination={true}
        queryTotalCount={totalElements}
        queryPageCount={totalPages}
        queryPageIndex={queryPageIndex}
        setQueryPageIndex={(value) => manageDataTableState({ queryPageIndex: value })}
        queryPageSize={queryPageSize}
        setQueryPageSize={(value) => manageDataTableState({ queryPageSize: value })}
        queryPageSortBy={queryPageSortBy}
        setQueryPageSortBy={(value) => manageDataTableState({ queryPageSortBy: value })}
        filterObj={filterObj}
        setFilterObj={(newFilterObj) => manageDataTableState({
          filterObj: newFilterObj,
          queryPageIndex: 0
        })}
        enableFilters={true}
        enableSorting={true}
        containerHeight="var(--chakra-sizes-2xl)"
      />

      <DeleteDialog
        isOpen={isConfirmDialogOpen}
        onClose={onConfirmDialogClose}
        onDelete={handleCategoryDelete}
        entityName={"Category"}
      />
    </Box>
  );
}

export default SupplierCategories;